import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQueryGetMe } from "@/apis/users/getMe";
import { useQueryGetExamPaper } from "@/apis/exam_papers/getExamPaper";
import type { IQuestion } from "@/apis/exam_papers/types";
import ModuleNavigationModal, {
  type ModuleInfo,
} from "@/components/exam/shared/ModuleNavigationModal";
import ResultQuestionSelectorPopover from "@/pages/exam_results/components/ResultQuestionSelectorPopover";
import HighlightableContent from "@/pages/my_exams/components/HighlightableContent";
import MathShortAnswerDirections from "@/pages/my_exams/components/MathShortAnswerDirections";
import ExamHeaderUI from "@/components/exam/ui/ExamHeaderUI";
import ExamFooterUI from "@/components/exam/ui/ExamFooterUI";
import ExamPassageUI from "@/components/exam/ui/ExamPassageUI";
import ExamQuestionPanelUI from "@/components/exam/ui/ExamQuestionPanelUI";
import {
  EXAM_PAPER_TYPE_READING_WRITING,
  EXAM_PAPER_TYPE_MATH,
} from "@/constants/exam";
import { getPaperTypeLabel } from "@/constants/exam";
import { getSectionLabel } from "@/utils/exam";

const ExamPreviewPage: React.FC = () => {
  const { id, module } = useParams<{ id: string; module: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const questionParam = searchParams.get("question");
  const { data: me, isLoading: isMeLoading } = useQueryGetMe();
  const { data: examPaper, isLoading: isPaperLoading } = useQueryGetExamPaper(
    id || ""
  );

  // UI States
  const [moduleNavOpen, setModuleNavOpen] = useState(false);
  const [currentModuleId, setCurrentModuleId] = useState(module || "1");
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(
    questionParam ? Number(questionParam) - 1 : 0
  );
  const [questionSelectorOpen, setQuestionSelectorOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Client-side only states for preview
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<string, string>
  >({});
  const [excludedChoices, setExcludedChoices] = useState<
    Record<string, string[]>
  >({});
  const [markedForReview, setMarkedForReview] = useState<
    Record<string, boolean>
  >({});
  const [highlights, setHighlights] = useState<
    Record<string, { passage?: string; question?: string }>
  >({});
  const [shortAnswers, setShortAnswers] = useState<Record<string, string>>({});
  const [excludeMode, setExcludeMode] = useState(false);

  // Extract modules from exam paper
  const sectionLabel = getSectionLabel(examPaper?.exam_type);
  const modules: ModuleInfo[] = useMemo(() => {
    if (!examPaper?.sections) {
      return [];
    }

    return examPaper.sections.map((section, index) => ({
      id: (index + 1).toString(),
      title: `${sectionLabel} ${index + 1}`,
      questionCount: section.questions?.length || 0,
    }));
  }, [examPaper, sectionLabel]);

  // Get current section and questions
  const sectionIndex = Number(currentModuleId) - 1;
  const currentSection = examPaper?.sections?.[sectionIndex];
  const questions: IQuestion[] = currentSection?.questions || [];
  const currentQuestion = questions[currentQuestionIdx];

  // Update URL when question changes
  useEffect(() => {
    if (currentQuestionIdx >= 0) {
      setSearchParams(
        { question: String(currentQuestionIdx + 1) },
        { replace: true }
      );
    }
  }, [currentQuestionIdx, setSearchParams]);

  // Update currentQuestionIdx when URL changes
  useEffect(() => {
    const newQuestionIdx = questionParam ? Number(questionParam) - 1 : 0;
    if (
      newQuestionIdx !== currentQuestionIdx &&
      newQuestionIdx >= 0 &&
      newQuestionIdx < questions.length
    ) {
      setCurrentQuestionIdx(newQuestionIdx);
    }
  }, [questionParam, questions.length]);

  // Close popover on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setQuestionSelectorOpen(false);
      }
    }
    if (questionSelectorOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [questionSelectorOpen]);

  // Event handlers
  const handleModuleSelect = (moduleId: string) => {
    setCurrentModuleId(moduleId);
    setCurrentQuestionIdx(0);
    setSearchParams({ question: "1" }, { replace: true });
  };

  const handleQuestionSelect = (idx: number) => {
    setCurrentQuestionIdx(idx);
  };

  const handleAnswerSelect = (answer: string) => {
    if (!currentQuestion) return;
    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: answer,
    }));
  };

  const handleChoiceExclude = (choiceId: string) => {
    if (!currentQuestion) return;
    setExcludedChoices((prev) => {
      const currentExcluded = prev[currentQuestion.id] || [];
      const isExcluded = currentExcluded.includes(choiceId);
      return {
        ...prev,
        [currentQuestion.id]: isExcluded
          ? currentExcluded.filter((id) => id !== choiceId)
          : [...currentExcluded, choiceId],
      };
    });
  };

  const handleMarkForReview = () => {
    if (!currentQuestion) return;
    setMarkedForReview((prev) => ({
      ...prev,
      [currentQuestion.id]: !prev[currentQuestion.id],
    }));
  };

  const handleShortAnswerChange = (value: string) => {
    if (!currentQuestion) return;
    setShortAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: value,
    }));
    // Also update selectedAnswers for consistency
    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: value,
    }));
  };

  // Highlight save callback (client-side only)
  const handleSaveHighlight = useCallback(
    (contentType: "passage" | "question", newContent: string) => {
      if (!currentQuestion) return;

      setHighlights((prev) => ({
        ...prev,
        [currentQuestion.id]: {
          ...prev[currentQuestion.id],
          [contentType]: newContent,
        },
      }));
    },
    [currentQuestion]
  );

  const handleToggleExcludeMode = () => {
    setExcludeMode((prev) => !prev);
  };

  // Calculate question states for popover
  const answeredQuestions = useMemo(() => {
    return questions
      .map((q, idx) => {
        const hasMultipleChoiceAnswer = selectedAnswers[q.id];
        const hasShortAnswer = shortAnswers[q.id]?.trim();
        return hasMultipleChoiceAnswer || hasShortAnswer ? idx : -1;
      })
      .filter((idx) => idx !== -1);
  }, [questions, selectedAnswers, shortAnswers]);

  const forReviewQuestions = useMemo(() => {
    return questions
      .map((q, idx) => (markedForReview[q.id] ? idx : -1))
      .filter((idx) => idx !== -1);
  }, [questions, markedForReview]);

  // Prepare directions content
  const directionsContent = useMemo(() => {
    if (!examPaper) return null;

    const subject = examPaper.subject || EXAM_PAPER_TYPE_READING_WRITING;

    if (subject === EXAM_PAPER_TYPE_READING_WRITING) {
      return (
        <p className="text-lg">
          The questions in this section address a number of important reading
          and writing skills. Each question includes one or more passages, which
          may include a table or graph. Read each passage and question
          carefully, and then choose the best answer to the question based on
          the passage(s).
          <br />
          <br />
          All questions in this section are multiple-choice with four answer
          choices. Each question has a single best answer.
        </p>
      );
    }

    if (subject === EXAM_PAPER_TYPE_MATH) {
      return (
        <p className="text-lg">
          The questions in this section address a number of important math
          skills.
          <br />
          Use of a calculator is permitted for all questions. A reference sheet,
          calculator, and these directions can be accessed throughout the test.
          <br />
          Unless otherwise indicated:
          <br />
          • All variables and expressions represent real numbers.
          <br />
          • Figures provided are drawn to scale.
          <br />
          • All figures lie in a plane.
          <br />• The domain of a given function f is the set of all real
          numbers x for which f(x) is a real number.
        </p>
      );
    }

    return null;
  }, [examPaper]);

  // Loading states
  if (isMeLoading || isPaperLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#f7f8fa]">
        <div className="text-lg">Loading exam preview...</div>
      </div>
    );
  }

  // Error states
  if (!examPaper || !currentSection || questions.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#f7f8fa]">
        <div className="text-center">
          <div className="text-red-600 mb-4">No exam paper data available</div>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Get current subject for header
  const currentSubject = examPaper.exam_type || "SAT";
  const currentSubjectName =
    examPaper.subject || EXAM_PAPER_TYPE_READING_WRITING;
  const sectionTitle = `${getPaperTypeLabel(
    currentSubjectName
  )}, ${getSectionLabel(currentSubject)} ${currentModuleId}`;

  // Get highlighted content (client-side only)
  const questionHighlights = highlights[currentQuestion?.id || ""];
  const highlightedPassage =
    questionHighlights?.passage || currentQuestion?.passage || "";
  const highlightedQuestion =
    questionHighlights?.question || currentQuestion?.question || "";

  return (
    <div className="flex flex-col h-screen bg-[#f7f8fa]">
      {/* Header */}
      <ExamHeaderUI
        sectionTitle={sectionTitle}
        directionsContent={directionsContent}
        mode="demo"
        showControls={true}
        onSwitchModule={() => setModuleNavOpen(true)}
      />

      {/* Main Content */}
      <main className="flex-1 flex h-0 min-h-0 overflow-hidden bg-white justify-center">
        <ExamPassageUI
          passage={highlightedPassage}
          subject={currentSubjectName}
          examType={currentSubject}
          answerType={
            currentQuestion?.answer_type as "multiple_choice" | "short_answer"
          }
          mode="demo"
          mathDirectionsContent={<MathShortAnswerDirections />}
          highlightableContent={
            currentSubjectName === EXAM_PAPER_TYPE_MATH ? undefined : (
              <HighlightableContent
                content={highlightedPassage}
                contentType="passage"
                onSaveHighlight={handleSaveHighlight}
                className="max-w-[650px] w-full"
              />
            )
          }
          onSaveHighlight={handleSaveHighlight}
        />
        <ExamQuestionPanelUI
          questionNumber={currentQuestionIdx + 1}
          questionText={highlightedQuestion}
          answerType={
            (currentQuestion?.answer_type as
              | "multiple_choice"
              | "short_answer") || "multiple_choice"
          }
          choices={currentQuestion?.choices}
          selectedChoice={selectedAnswers[currentQuestion?.id || ""]}
          excludedChoices={excludedChoices[currentQuestion?.id || ""] || []}
          markForReview={markedForReview[currentQuestion?.id || ""] || false}
          shortAnswer={shortAnswers[currentQuestion?.id || ""] || ""}
          excludeMode={excludeMode}
          mode="demo"
          onSelectChoice={handleAnswerSelect}
          onExcludeChoice={handleChoiceExclude}
          onToggleMarkForReview={handleMarkForReview}
          onToggleExcludeMode={handleToggleExcludeMode}
          onShortAnswerChange={handleShortAnswerChange}
          highlightableContent={
            <HighlightableContent
              content={highlightedQuestion}
              contentType="question"
              onSaveHighlight={handleSaveHighlight}
              className="prose prose-lg leading-relaxed"
            />
          }
        />
      </main>

      {/* Footer */}
      <div ref={popoverRef}>
        <ExamFooterUI
          studentName={me?.name || "Student"}
          currentQuestionIdx={currentQuestionIdx}
          totalQuestions={questions.length}
          mode="demo"
          showQuestionSelector={true}
          answeredQuestions={answeredQuestions}
          forReviewQuestions={forReviewQuestions}
          questionSelectorOpen={questionSelectorOpen}
          onToggleQuestionSelector={() =>
            setQuestionSelectorOpen(!questionSelectorOpen)
          }
          onQuestionSelect={handleQuestionSelect}
          onPrev={() =>
            handleQuestionSelect(Math.max(0, currentQuestionIdx - 1))
          }
          onNext={() =>
            handleQuestionSelect(
              Math.min(questions.length - 1, currentQuestionIdx + 1)
            )
          }
          disablePrev={currentQuestionIdx === 0}
          disableNext={currentQuestionIdx === questions.length - 1}
          nextButtonText="Next"
          questionSelectorContent={
            <ResultQuestionSelectorPopover
              isOpen={questionSelectorOpen}
              onClose={() => setQuestionSelectorOpen(false)}
              sectionTitle={sectionTitle}
              totalQuestions={questions.length}
              answeredQuestions={answeredQuestions}
              forReviewQuestions={forReviewQuestions}
              onQuestionSelect={handleQuestionSelect}
              currentQuestionIdx={currentQuestionIdx}
            />
          }
        />
      </div>

      {/* Module Navigation Modal */}
      <ModuleNavigationModal
        isOpen={moduleNavOpen}
        onClose={() => setModuleNavOpen(false)}
        modules={modules}
        currentModuleId={currentModuleId}
        onModuleSelect={handleModuleSelect}
      />
    </div>
  );
};

export default ExamPreviewPage;
